/**
 * User Preferences Store
 *
 * Manages user display and data preferences that persist across sessions.
 * Syncs with backend when authenticated, falls back to localStorage.
 *
 * Theme & sidebar changes are pushed to ThemeContext and navigationStore
 * via a Zustand subscriber (see bottom of file).
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api } from '@/lib/api'

// Types
export interface UserPreferences {
  timezone: string
  dateFormat: 'YYYY-MM-DD' | 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'DD-MMM-YYYY'
  timeFormat: '12h' | '24h'
  numberFormat: 'en-US' | 'en-GB' | 'de-DE' | 'fr-FR'
  theme: 'light' | 'dark' | 'system'
  compactMode: boolean
  defaultPageSize: number
  sidebarCollapsed: boolean
  defaultExportFormat: 'csv' | 'excel' | 'pdf'
}

// Default preferences
export const DEFAULT_PREFERENCES: UserPreferences = {
  timezone: 'UTC',
  dateFormat: 'YYYY-MM-DD',
  timeFormat: '24h',
  numberFormat: 'en-US',
  theme: 'system',
  compactMode: false,
  defaultPageSize: 25,
  sidebarCollapsed: false,
  defaultExportFormat: 'csv',
}

interface UserPreferencesState {
  // Preferences data
  preferences: UserPreferences
  isLoading: boolean
  isSaving: boolean
  error: string | null
  lastSynced: string | null

  // Actions
  fetchPreferences: () => Promise<void>
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>
  setPreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void
  resetToDefaults: () => Promise<void>
  clearError: () => void
}

export const useUserPreferencesStore = create<UserPreferencesState>()(
  persist(
    (set, get) => ({
      // Initial state
      preferences: DEFAULT_PREFERENCES,
      isLoading: false,
      isSaving: false,
      error: null,
      lastSynced: null,

      // Fetch preferences from backend
      fetchPreferences: async () => {
        set({ isLoading: true, error: null })

        try {
          const response = await api.get<UserPreferences>('/users/me/preferences')
          set({
            preferences: { ...DEFAULT_PREFERENCES, ...response.data },
            isLoading: false,
            lastSynced: new Date().toISOString(),
          })
        } catch (error) {
          console.error('Failed to fetch preferences:', error)
          set({
            isLoading: false,
            error: 'Failed to load preferences',
          })
        }
      },

      // Update preferences on backend
      updatePreferences: async (updates) => {
        const { preferences } = get()
        const optimisticUpdate = { ...preferences, ...updates }

        // Optimistic update
        set({ preferences: optimisticUpdate, isSaving: true, error: null })

        try {
          // Convert camelCase to snake_case for API
          const snakeCaseUpdates: Record<string, unknown> = {}
          for (const [key, value] of Object.entries(updates)) {
            const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
            snakeCaseUpdates[snakeKey] = value
          }

          const response = await api.put<UserPreferences>('/users/me/preferences', snakeCaseUpdates)
          set({
            preferences: { ...DEFAULT_PREFERENCES, ...response.data },
            isSaving: false,
            lastSynced: new Date().toISOString(),
          })
        } catch (error) {
          // Rollback on error
          set({
            preferences,
            isSaving: false,
            error: 'Failed to save preferences',
          })
          throw error
        }
      },

      // Set a single preference (local only, doesn't sync)
      setPreference: (key, value) => {
        const { preferences } = get()
        set({ preferences: { ...preferences, [key]: value } })
      },

      // Reset to defaults
      resetToDefaults: async () => {
        set({ isSaving: true, error: null })

        try {
          const response = await api.put<UserPreferences>('/users/me/preferences', {
            timezone: DEFAULT_PREFERENCES.timezone,
            date_format: DEFAULT_PREFERENCES.dateFormat,
            time_format: DEFAULT_PREFERENCES.timeFormat,
            number_format: DEFAULT_PREFERENCES.numberFormat,
            theme: DEFAULT_PREFERENCES.theme,
            compact_mode: DEFAULT_PREFERENCES.compactMode,
            default_page_size: DEFAULT_PREFERENCES.defaultPageSize,
            sidebar_collapsed: DEFAULT_PREFERENCES.sidebarCollapsed,
            default_export_format: DEFAULT_PREFERENCES.defaultExportFormat,
          })
          set({
            preferences: { ...DEFAULT_PREFERENCES, ...response.data },
            isSaving: false,
            lastSynced: new Date().toISOString(),
          })
        } catch (error) {
          set({
            isSaving: false,
            error: 'Failed to reset preferences',
          })
          throw error
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'user-preferences-storage',
      partialize: (state) => ({
        preferences: state.preferences,
        lastSynced: state.lastSynced,
      }),
    }
  )
)

// Selector hooks for convenience (only the ones that are actually used)
export const useCompactMode = () => useUserPreferencesStore((state) => state.preferences.compactMode)
export const useDateFormat = () => useUserPreferencesStore((state) => state.preferences.dateFormat)
export const useTimeFormat = () => useUserPreferencesStore((state) => state.preferences.timeFormat)
export const useNumberFormat = () => useUserPreferencesStore((state) => state.preferences.numberFormat)
export const useDefaultPageSize = () => useUserPreferencesStore((state) => state.preferences.defaultPageSize)

/**
 * Subscriber: sync theme & sidebar changes to ThemeContext and navigationStore.
 *
 * This runs outside React — it uses Zustand's subscribe() to watch for
 * preferences.theme and preferences.sidebarCollapsed changes, then pushes
 * them to the relevant stores/contexts.
 *
 * ThemeContext sync happens in PreferencesHydrator (React component) because
 * ThemeContext is a React context that requires useContext().
 * Sidebar sync can happen here since navigationStore is a Zustand store.
 */
/**
 * Cross-store sync is handled by PreferencesHydrator (in App.tsx) to avoid
 * circular imports between userPreferencesStore and navigationStore.
 */
